import { Container, Typography, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useMemo, useEffect, useState } from 'react';
import _Slider from 'react-slick';
const Slider = (_Slider as any).default || _Slider;
import { useQuery } from '@apollo/client/react';
import gql from 'graphql-tag';
import useMediaQuery from '@mui/material/useMediaQuery';
import { withApollo } from '../../../hoc/withApollo';
import CardSliderEvent from '../../../components/cards/CardSliderEvent';
import Link from '../../../components/Link';
import { RRule } from 'rrule';
import moment from 'moment';
import { rruleToText } from '../../../utils/utils';

const useStyles = makeStyles()((theme) => ({
  cardTitle: {
    color: '#2C367E',
    fontFamily: theme.typography.h5.fontFamily,
    textTransform: 'uppercase',
    fontWeight: '400',
    textAlign: 'center',
    fontSize: '2.2em',
    [theme.breakpoints.down('md')]: { fontSize: '1.4em' },
  },
  border: {
    width: '3em',
    borderColor: '#2C367E',
    borderBottom: 'solid',
    marginLeft: '48%',
    borderBottomColor: '#2C367E',
    color: '#2C367E',
    height: '1em',
    textAlign: 'center',
  },
  buttonGrid: {
    fontSize: '1.2em',
    margin: '1.5em 0 1.5em 0 ',
    color: 'white',
    backgroundColor: '#2C367E',
    border: 'none',
    fontWeight: 'bold',
    borderRadius: '1.5em',
    padding: '0.2em 3em 0.2em 3em',
    minHeight: '2.5em',
    '&:hover': {
      cursor: 'pointer',
      color: '#2C367E',
      backgroundColor: 'white',
      border: '2px solid #2C367E',
    },
    backgroundImage: "url('/arrow.svg')",
    backgroundRepeat: 'no-repeat',
    backgroundPositionX: '5px',
    backgroundPositionY: '-3px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.2em',
      margin: '1.5em 0 1.5em 0',
      padding: '0.2em 3em 0.2em 3em',
      minHeight: '2.5em',
    },
  },
  articleCarroussel: {
    paddingTop: '2em',
    '& .slick-list': { padding: '8px 0' },
    '& .slick-slide': { padding: '0 8px', boxSizing: 'border-box' },
    '& .slick-slide > div': { display: 'flex', justifyContent: 'center' },
    [theme.breakpoints.down('md')]: { '& .slick-slide': { padding: '0 4px' } },
  },
  mobileHint: {
    marginTop: '0.5em',
    color: '#2C367E',
    fontSize: '0.95rem',
    fontStyle: 'italic',
  },
  buttonArticle: { paddingTop: '1em', paddingBottom: '1em' },
  event: {
    width: '100%',
    paddingTop: '3em',
    paddingBottom: '3em',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    backgroundImage: "url('/icons/calendar-home.svg')",
    backgroundSize: '30%',
    backgroundPosition: 'right',
    backgroundPositionY: 'bottom',
    backgroundRepeat: 'no-repeat',
    [theme.breakpoints.down('md')]: {
      paddingTop: '2em',
      paddingBottom: '2em',
      backgroundSize: '42%',
    },
  },
}));

const GET_EVENTS = gql`
  query events($limit: Int, $sort: String, $way: String, $startingDate: String) {
    events(limit: $limit, sort: $sort, way: $way, startingDate: $startingDate) {
      id
      label
      startedAt
      endedAt
      published
      lat
      lng
      address
      city
      dateRule
      url
      entries {
        label
        icon
        collection { code label }
        parentEntry { code label color collection { code label } }
      }
      actors { id name }
      pictures {
        id
        label
        originalPicturePath
        originalPictureFilename
        position
        logo
        main
      }
    }
  }
`;

interface LastEventProps {
  id?: string;
}

const LastEvent = ({ id }: LastEventProps) => {
  const [eventToRender, setEventToRender] = useState<{ eventData: any } | null>(null);
  const date = new Date();
  date.setHours(0, 0, 0, 0);

  const { data: eventData } = useQuery(GET_EVENTS, {
    variables: { sort: 'startedAt', way: 'ASC', startingDate: date.toISOString() },
  });

  useEffect(() => {
    setEventToRender({ eventData });
  }, [eventData]);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { classes: styles } = useStyles();
  const maxImageDisplay = !mobile ? 5 : 1;

  const SampleNextArrow = (props: any) => (
    <div className={props.className} style={{ ...props.style, display: 'block' }} onClick={props.onClick} />
  );
  const SamplePrevArrow = (props: any) => (
    <div className={props.className} style={{ ...props.style, display: 'block' }} onClick={props.onClick} />
  );

  const compare = (a: any, b: any) => {
    if (a.startedAt > b.startedAt) return 1;
    if (a.startedAt < b.startedAt) return -1;
    return 0;
  };

  const getAllEventsFromRecurringEvent = (event: any) => {
    const startEventDate = moment(parseInt(event.startedAt));
    const endEventDate = moment(parseInt(event.endedAt));
    const { dateRule } = event;
    const rrule = RRule.fromString(
      `DTSTART:${startEventDate.format('YYYYMMDD[T]HHmmss[Z]')}\nRRULE:${dateRule}`,
    );
    return rrule.between(new Date(), moment().add(1, 'month').toDate()).map((d) => {
      const text = rruleToText(rrule);
      const startTimeFormat = startEventDate.format('HH[h]mm');
      const endTimeFormat = endEventDate.format('HH[h]mm');
      return {
        ...event,
        startedAt: moment(d).valueOf().toString(),
        duration: `${text} de ${startTimeFormat} à ${endTimeFormat}`,
      };
    });
  };

  const sameDay = (date1: string, date2: string) => {
    const d1 = new Date(parseInt(date1));
    const d2 = new Date(parseInt(date2));
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const events = useMemo(() => {
    const initialEvents = eventToRender?.eventData?.events || [];
    const recurringEvents = initialEvents.filter((e: any) => e.dateRule);
    const allRecurringEvents = recurringEvents.map((e: any) => getAllEventsFromRecurringEvent(e));
    return initialEvents
      .filter((e: any) => !e.dateRule)
      .concat(allRecurringEvents.reduce((acc: any[], items: any[]) => acc.concat(items), []))
      .filter((e: any) => moment(parseInt(e.startedAt)) > moment().startOf('day'));
  }, [eventToRender]);

  const sortedEvents = useMemo(() => {
    const localEvents: any[] = [];
    events?.forEach((event: any) => {
      if (!sameDay(event.startedAt, event.endedAt)) {
        const nbDayEvent = moment(new Date(parseInt(event.endedAt))).diff(
          moment(new Date(parseInt(event.startedAt))),
          'days',
        );
        if (nbDayEvent >= 1) {
          moment.locale('fr');
          const durLabel =
            `Du ` +
            moment(new Date(parseInt(event.startedAt))).format('DD MMMM YYYY') +
            ` au ` +
            moment(new Date(parseInt(event.endedAt))).format('DD MMMM YYYY');
          event = Object.assign({ duration: durLabel }, event);
          const newEventForOtherDay = { ...event, duration: durLabel };
          newEventForOtherDay.startedAt = moment(new Date(parseInt(event.startedAt)))
            .add(nbDayEvent, 'days')
            .toDate();
          localEvents.push(newEventForOtherDay);
        }
      }
      localEvents.push(event);
    });
    return localEvents.sort(compare);
  }, [events]);

  const eventsToDisplay = useMemo(() => {
    if (!mobile) return sortedEvents;
    return sortedEvents.slice(0, 3);
  }, [mobile, sortedEvents]);

  const settings = {
    dots: true,
    infinite: true,
    slidesToShow:
      eventToRender?.eventData && eventToRender.eventData.events.length > maxImageDisplay
        ? maxImageDisplay
        : eventToRender?.eventData && eventToRender.eventData.events.length,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
  };

  return (
    <Container className={styles.event} id={id}>
      <Typography variant="h5" className={styles.cardTitle}>
        LES ÉVÉNEMENTS À VENIR
      </Typography>
      <div className={styles.border} />
      <Slider {...settings} className={styles.articleCarroussel}>
        {eventsToDisplay.map((event: any, index: number) => (
          <CardSliderEvent key={`${event.id}-${event.startedAt}-${index}`} event={event} />
        ))}
      </Slider>
      {mobile && sortedEvents.length > eventsToDisplay.length && (
        <Typography className={styles.mobileHint}>
          Tu as vu les prochains événements. Clique sur "Voir l'agenda complet" pour découvrir les
          autres.
        </Typography>
      )}
      <div className={styles.buttonArticle}>
        <Link href="/agenda">
          <button className={styles.buttonGrid}>VOIR L'AGENDA COMPLET</button>
        </Link>
      </div>
    </Container>
  );
};

export default withApollo()(LastEvent);
